import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const PopupCloseContext = createContext<(() => void) | null>(null);

export const PopupCloseProvider = PopupCloseContext.Provider;

interface OpenProps<A extends unknown[]> {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean, ...rest: A) => void;
}

export function usePopupOpen<A extends unknown[]>({ open, defaultOpen, onOpenChange }: OpenProps<A>) {
  const [inner, setInner] = useState(defaultOpen ?? false);
  const controlled = open !== undefined;
  const handleOpenChange = useCallback(
    (next: boolean, ...rest: A) => {
      if (!controlled) setInner(next);
      onOpenChange?.(next, ...rest);
    },
    [controlled, onOpenChange],
  );
  const close = useCallback(() => handleOpenChange(false, ...([] as unknown as A)), [handleOpenChange]);
  return { open: controlled ? open : inner, onOpenChange: handleOpenChange, close };
}

export function useCloseWhenAnchorHidden(own?: () => void) {
  const fromRoot = useContext(PopupCloseContext);
  const close = own ?? fromRoot;
  const [positioner, setPositioner] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!positioner || !close) return;
    const check = () => {
      if (positioner.hasAttribute('data-anchor-hidden')) close();
    };
    const mo = new MutationObserver(check);
    mo.observe(positioner, { attributes: true, attributeFilter: ['data-anchor-hidden'] });
    return () => mo.disconnect();
  }, [positioner, close]);
  return setPositioner;
}
